import { useImageFallback } from '@/hooks/useImageFallback';

interface ProfileAvatarProps {
  src: string | null;
  firstName: string;
  lastName: string;
  size?: number;
  showUpload?: boolean;
  onUpload?: () => void;
}

function getInitials(firstName: string, lastName: string): string {
  const first = (firstName.charAt(0) || '').toUpperCase();
  const last = (lastName.charAt(0) || '').toUpperCase();
  return `${first}${last}`;
}

export function ProfileAvatar({
  src,
  firstName,
  lastName,
  size = 80,
  showUpload = false,
  onUpload,
}: ProfileAvatarProps) {
  const { hasError, onError } = useImageFallback(src);
  const initials = getInitials(firstName, lastName);
  const showImage = src && !hasError;
  // Generic alt — never interpolate the person's name. Session replay
  // records element attributes, which the `maskTextSelector` baseline does
  // not mask, so a name in `alt` would leak into recordings (issue #252).
  // The display name is shown as adjacent visible text for sighted users.
  const alt = 'Profile photo';

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      data-testid="profile-avatar"
    >
      <div
        className="w-full h-full rounded-full overflow-hidden flex items-center justify-center bg-stone-200 text-stone-600 font-bold"
        style={{ fontSize: Math.round(size * 0.32) }}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={onError}
          />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      {showUpload && (
        <button
          type="button"
          onClick={onUpload}
          aria-label="Upload photo"
          data-testid="profile-avatar-upload"
          className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-wolisho-brown border-2 border-wolisho-cream flex items-center justify-center text-white hover:opacity-100 opacity-90 transition-opacity"
        >
          <svg width="14" height="14" viewBox="0 0 30 30" fill="none" aria-hidden="true">
            <path
              d="M25.4 6h-4.3l-1.3-1.6c-.8-1-2-1.5-3.2-1.5h-3.2c-1.3 0-2.5.6-3.3 1.5L8.9 6H4.6C2.4 6 .5 7.9.5 10.2v12.4c0 2.3 1.9 4.1 4.1 4.1h20.8c2.3 0 4.1-1.9 4.1-4.1V10.2C29.5 7.9 27.6 6 25.4 6zM15 21.6c-2.9 0-5.2-2.3-5.2-5.2s2.3-5.2 5.2-5.2 5.2 2.3 5.2 5.2-2.3 5.2-5.2 5.2z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
